import type { IconName } from '@purpur/library/icon';
import type { AxiosError } from 'axios';

import { SapRatePlan, ServiceFeature } from './EshopSapOfferModel';

export enum CheckoutPaymentBank {
    Swedbank = 'SWEDBANK',
    Seb = 'SEB',
    Luminor = 'DNBNORD',
    Siauliu = 'SIAULIU',
}

export interface ProductReferences {
    references: ProductReference[];
}

export type LocalEnvironmentResponse = {
    isLocal: boolean;
};

export type MetaElement = {
    content: string;
    property?: string;
    name?: string;
};

// canonical link annotation doc: https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls
export type CanonicalLink = {
    rel: string;
    href: string;
};

export type ProductMetadata = {
    jsonScripts: string[];
    metaElements: MetaElement[];
    links: CanonicalLink[];
};

interface ProductReference {
    referenceType: string;
    target: ProductVariant;
}

interface AddProductToCartResponse {
    entry: {
        entryNumber: number;
        product: Partial<ProductVariant>;
        quantity: number;
        totalPrice: {
            currencyIso: string;
            value: number;
        };
    };
    quantity: number;
    quantityAdded: number;
    statusCode: AddProductToCartResponseStatusCodeType;
}

export interface CartModification extends AddProductToCartResponse {
    cart: Cart;
}

export type CartId = string;

export type CartIdParam = {
    cartId: CartId;
};

export type CartCodeParam = {
    cartCode: string;
};

export interface RemoveProductParams extends CartIdParam {
    entryNumber: number;
}

export type AddProductBody = {
    product: {
        code: string;
    };
    entryType: CartEntryType;
    quantity?: number;
};

export interface MigrateCartParams {
    oldCartId?: string;
    type?: string;
    serviceUoId?: string;
    serviceSoId?: string;
    UOType?: string;
    gifts?: string;
    disney?: string;
    netflix?: string;
}

type DeliveryModeId = {
    deliveryModeId: string;
};

export type SetAddressesBodyB2B = CombinedAddressesB2B & DeliveryModeId;

export type SetDeliveryModeParams = CartIdParam & DeliveryModeId;

export enum ContractType {
    WithPlan = 'WITH_PLAN',
    WithoutPlan = 'WITHOUT_PLAN',
    Renting = 'RENTING',
}

export enum CartStepsEnum {
    LogIn = 'LOG_IN',
    Identification = 'IDENTIFICATION',
    ClientDataStep = 'CLIENT_DATA_STEP',
    ContractTypeSelected = 'CONTRACT_TYPE_SELECTED',
    ClientDataNotEntered = 'CLIENT_DATA_NOT_ENTERED',
    DeliveryStep = 'DELIVERY_STEP',
    DocumentsStep = 'DOCUMENTS_STEP',
    NoDeliveryInfo = 'NO_DELIVERY_INFO',
    PaymentStep = 'PAYMENT_STEP',
    SentToBank = 'SENT_TO_BANK',
}

export type RegisterCartStepParams = {
    cartStepEnum: CartStepsEnum;
    contractType?: CustomerContractOption;
} & CartIdParam;

export type CreditCheckResponse = {
    approved: boolean;
    checkMessage: string;
    checkRequired: boolean;
    checkSuccessful: boolean;
    creditLimitFail: boolean;
    externalDebtFail: boolean;
    internalDebtFail: boolean;
    otherCheckFail: boolean;
};

export enum CustomerContractOption {
    New = 'NEW',
    Existing = 'EXISTING',
    ExtendSAP = 'EXTEND_SAP', // TODO: will be replaced with NO_CONTRACT_SAP after full release of LTCONART-155
    Migrate = 'MIGRATE',
    NoContractSAP = 'NO_CONTRACT_SAP',
    NoContract = 'NO_CONTRACT',
}

type SaveNewPlanOrderBody = {
    customerContractOption: CustomerContractOption.New;
    contactPhone: string;
    contactEmail: string;
    cardType: string;
};

type SaveExistingPlanOrderBody = {
    customerContractOption: CustomerContractOption.Existing;
    migratePhoneNumber: string;
    contractType?: ContractType;
};

type SaveMigratePlanOrderBody = {
    customerContractOption: CustomerContractOption.Migrate;
    migratePhoneNumber: string;
    migrateTelcoOperatorCode: string;
    cardType: string;
};

type SaveNoContractPlanOrderBody = {
    customerContractOption: CustomerContractOption.NoContract;
    contactPhone: string;
    contactEmail: string;
    contactName: string;
};

type SaveSAPExtendPlanOrderBody = {
    customerContractOption: CustomerContractOption.ExtendSAP | CustomerContractOption.NoContractSAP;
    contactPhone: string;
    contactEmail: string;
    contactName: string;
};
type SavePlanToCartBody = {
    customerContractOption: CustomerContractOption;
    contractType?: ContractType;
};
type SavePlanContractTypeBody = {
    contractType: ContractType;
};

export type SavePlanOrderBody =
    | SaveExistingPlanOrderBody
    | SaveSAPExtendPlanOrderBody
    | SaveNewPlanOrderBody
    | SaveMigratePlanOrderBody
    | SaveNoContractPlanOrderBody
    | SavePlanToCartBody
    | SavePlanContractTypeBody;

export type SavePlanOrderData = Omit<SaveExistingPlanOrderBody, 'customerContractOption'> &
    Omit<SaveNewPlanOrderBody, 'customerContractOption'> &
    Omit<SaveMigratePlanOrderBody, 'customerContractOption' | 'migrateTelcoOperatorCode'> &
    Omit<SaveNoContractPlanOrderBody, 'customerContractOption'> & {
        customerContractOption: CustomerContractOption;
        migrateTelcoOperator: TelcoOperator;
    };

export type CompanyData = {
    companyName: string;
    companyCode: string;
};

export type VatConfig = {
    registeredVatPayer: boolean;
    budgetaryInstitution: boolean;
};

export enum CartEntryType {
    PurchasableProduct = 'PURCHASABLE_PRODUCT',
    RatePlan = 'RATE_PLAN',
    UpsellProduct = 'UPSELL_PRODUCT',
    SimCard = 'SIM_CARD',
    EsimCard = 'ESIM_CARD',
    Warranty = 'WARRANTY',
    Netflix = 'NETFLIX',
    Disney = 'DISNEY',
}

enum AddProductToCartResponseStatusCodeType {
    Success = 'SUCCESS',
    LowStock = 'LOW_STOCK',
    NoStock = 'NO_STOCK',
    MaxOrderQuantityExceeded = 'MAX_ORDER_QUANTITY_EXCEEDED',
}

export type RatePlanCharacteristics = {
    dataAmount: number;
    dataAmountUnit: string;
    dataAmountInEu?: number;
    dataAmountInEuUnit?: string;
    dataAmountInOther?: number;
    dataAmountInOtherUnit?: string;
    includedSmsCount: number;
    includedMinuteCount: number;
    promotionDataAmount?: number;
};

type RatePlanPrice = {
    value: number;
    formattedValue: string;
    oldPrice: number;
    currency: string;
    hasPromotion: boolean;
    period: string;
};

export enum MobilityRatePlanType {
    Mhs = 'MHS',
    Mbb = 'MBB',
}

export interface ProductRatePlanInCart extends ProductVariantDiff, ProductDiff {
    insurancePossible: boolean;
    planCharacteristics: RatePlanCharacteristics;
    description?: string;
    mobilityRatePlanType: MobilityRatePlanType;
    legalText?: string;
    vasLegalText?: string;
}

export interface ProductRatePlan extends ProductRatePlanInCart {
    availableRecommendedServices: boolean;
    configurable: boolean;
    prices: RatePlanPrice[];
    vasProducts: VasProduct[];
}

export type VasProduct = {
    description?: string;
    iconType: string;
    title: string;
};

export enum UserAuthState {
    Anonymous = 'anonymous',
    AuthBlacklist = 'blacklist',
    AuthNotContractOwner = 'notContractOwner',
    Authorized = 'authorized',
    AuthWeakAccount = 'weakAccount',
}

export enum GiftRequirementType {
    Optional = 'OPTIONAL',
    AllMandatory = 'ALL_MANDATORY',
    OneMandatory = 'ONE_MANDATORY',
}
type LeasingPlanBase = {
    code: string;
    durationInMonths?: number;
    minimalInitialPayment?: Price;
    mobilityProductType?: MobilityProductType;
    monthlyPrice?: Price;
    name?: string;
    offerCode?: string;
    productOfferDiscount?: Price;
    standardDiscount?: Price;
    totalPriceWithDiscount?: Price;
};
type LeasingPlanHybris = LeasingPlanBase & {
    basic: true;
    origin: 'HYBRIS';
    sapOfferCode?: undefined;
};
type LeasingPlanSAP = LeasingPlanBase & {
    basic: false;
    origin: 'SAP';
    sapOfferCode: string;
};

export type LeasingPlan = LeasingPlanHybris | LeasingPlanSAP;

export interface Bank {
    id: CheckoutPaymentBank;
    title: string;
}

export enum CheckoutPaymentMethods {
    Bank = 'BANK',
    Cash = 'CASH',
    PostponedPayment = 'POSTPONED_PAYMENT',
    Installment = 'INSTALLMENT',
}

export type CheckoutPaymentMethod = `${CheckoutPaymentMethods}`;

export type PlaceOrderParam = {
    paymentType?: CheckoutPaymentMethod;
    consentTemplates?: ConsentTemplate[];
} & CartIdParam;

export interface BanklinkPaymentResponse {
    paymentData: BanklinkPaymentData;
}

interface BanklinkPaymentData {
    parameters: object;
    postUrl: string;
}

export interface DeliveryModesResponse {
    deliveryModes: DeliveryMode[];
}

type DeliveryAddress = {
    phone: string;
    extAddressNumber: string;
    email: string;
    courierNote: string;
};

type BillingAddress = {
    extAddressNumber: string;
};

export type CombinedAddresses = {
    deliveryAddress: DeliveryAddress;
    billingAddress: BillingAddress;
};

export type CombinedAddressesB2B = {
    deliveryAddress: DeliveryAddress;
    billingAddress: BillingAddress;
    isConsentAccepted: boolean;
    contactName: string;
};

export type TelcoOperator = { code: string; name: string };

export enum MobilityProductType {
    Phone = 'PHONE',
    RefurbishedPhone = 'REFURBISHED_PHONE',
    LandlinePhone = 'LANDLINE_PHONE',
    Accessory = 'ACCESSORY',
    EsimWatch = 'ESIM_WATCH',
    SmartGadget = 'SMART_GADGET',
    Modem = 'MODEM',
    Sim = 'SIM',
    Esim = 'ESIM',
    Computer = 'COMPUTER',
    LaptopComputer = 'LAPTOP_COMPUTER',
    WorkplaceKit = 'WORKPLACE_KIT',
    Tablet = 'TABLET',
    ConferenceEquipment = 'CONFERENCE_EQUIPMENT',
    Printer = 'PRINTER',
    Monitor = 'MONITOR',
    Tv = 'TV',
    GameConsole = 'GAME_CONSOLE',
}

/// ////////// Product ////////////
export const enum StockLevelStatuses {
    InStock = 'inStock',
    LowStock = 'lowStock',
    NoStock = 'outOfStock',
    FutureStock = 'futureStock',
}

export type StockLevelStatus = `${StockLevelStatuses}`;

export type Stock = {
    stockLevel: number;
    stockLevelStatus: StockLevelStatus;
    futureStock?: boolean;
    nextDeliveryDate?: string;
};

type GeneratedCart = {
    code: string;
    guid: string;
    withInsurance: boolean;
    generated: boolean;
};

export enum ImageFormat {
    Big = 'big',
    Category = 'category',
    Product = 'product',
    Thumbnail = 'thumbnail',
    Zoom = 'zoom',
}

export enum ImageType {
    Primary = 'PRIMARY',
    Gallery = 'GALLERY',
}

export type ProductImageData = {
    altText?: string;
    format: `${ImageFormat}`;
    imageType: `${ImageType}`;
    url: string;
    galleryIndex?: number;
};

type PriceBase = {
    formattedValue: string;
    shortFormattedValue: string;
    value: number;
};

export type Price = {
    currency: string;
    currencyIso: string;
    priceType: string;
    period?: string;
} & PriceBase;

type VariantValueCategory = {
    color?: string;
    name: string;
    sequence: number;
};

type ProductColor = {
    code: string;
    name: string;
};

export type Feature = {
    code: string;
    comparable: boolean;
    featureValues: FeatureValue[];
    labelDescription?: string;
    name: string;
    range: boolean;
    featureUnit?: FeatureUnit;
};

export type FeatureValue = {
    value: string;
};

type FeatureUnit = {
    name: string;
    symbol: string;
    unitType: string;
};

export type Classification = {
    code: string;
    features: Feature[];
    name: string;
};

type PreparedVariationData = VariantValueCategory & ProductOption;

export type PreparedVariation = {
    key: string;
    code: string;
    alias: ProductVariantAlias;
    isOutOfStock: boolean;
    data: PreparedVariationData;
    parentsKey?: string;
    depthLvl: number;
    pathFromRoot: string[];
    isLeaf: boolean;
    elements?: PreparedVariation[];
};

type SellingPriceTypeCodes = 'MONTHS_12' | 'MONTHS_24' | 'MONTHS_36' | 'FULL';

type PriceWithFee = {
    initialFee: number;
    sellingPriceType: {
        code: SellingPriceTypeCodes;
    };
} & Price;

type VariantOptionQualifier = {
    image: unknown;
    name: string;
    value: string;
    qualifier: string;
};
type ProductOption = {
    code: string;
    /** @see EntryBase.basePrice */
    priceData: Price;
    scalaId?: string;
    stock: Stock;
    url: string;
    variantOptionQualifiers?: VariantOptionQualifier[];
};

export type ProductVariantAlias =
    | 'COLOR'
    | 'SIZE'
    | 'DIAGONAL'
    | 'BAND_SIZE'
    | 'SCREEN_SIZE'
    | 'SCREEN_SIZE_IN'
    | 'SCREEN_SIZE_MM'
    | 'RING_SIZE'
    | 'RAM_CAPACITY'
    | 'INTERNET_CONNECTION';

type ParentVariantCategory = {
    alias: ProductVariantAlias;
    hasImage: boolean;
    name: string;
    priority: number;
};

export type VariantMatrix = {
    elements?: VariantMatrix[];
    isLeaf: boolean;
    parentVariantCategory: ParentVariantCategory;
    variantOption: ProductOption;
    variantValueCategory: VariantValueCategory;
};

type BaseOption = {
    options: ProductOption[];
    selected: ProductOption;
    variantType: string;
};

type Category = {
    code: string;
    name: string;
    url: string;
};

type MaxMinPrice = {
    currencyIso: string;
    value: number;
};
type PriceRange = {
    maxPrice: MaxMinPrice;
    minPrice: MaxMinPrice;
};

export type ProductRoot = {
    description: string;
    classifications: Classification[];
    multidimensional: boolean;
    price: Price;
    priceRange: PriceRange;
    prices: PriceWithFee[];
    summary: string;
    variantMatrix: VariantMatrix[];
    standardPrice: Price;
};

type ProductDiff = {
    availableForPickup: boolean;
    /** @name categories
     *  includes ProductVariant information, like screen size, storage, color etc. */
    categories: Category[];
    code: string;
    name: string;
    purchasable: boolean;
    /** @name stock
     * with SAP offers active, the stock status in ProductVariant/BaseProduct becomes incorrect,
     * to identify correct stock level refer to Cart.entries[PURCHASABLE_PRODUCT].product.stock
     * @see selectProductStock
     * @see selectIsProductOutOfStock */
    stock: Stock;
    url: string;
    scalaId: string;
    brand: string;
    category: Category;
    productColor: ProductColor;
    price: Price;
    storageSize: string;
    productType: MobilityProductType;
    insuranceVas?: InsuranceVAS;
    shortDescription: string;
    shortDescription2: string;
    shortDescription3: string;
    badges?: string[];
    energyClass?: string;
    energyConsumingSupported?: boolean;
    energyLabel?: { url: string };
    productInformationSheet?: { url: string };
    categoryUrl: string;
};

export interface BaseProduct extends ProductDiff, ProductRoot {
    variantOptions: ProductOption[];
    variantType: string;
    marketingTag?: {
        url: string;
        altText: string;
    };
}

type ProductVariantDiff = {
    baseOptions: BaseOption[];
    baseProduct: string;
    generatedCart: GeneratedCart;
    images: ProductImageData[];
    insurancePossible: boolean;
    productReferences: unknown[];
    reviews: unknown[];
    scalaId: string;
    volumePrices: unknown[];
};

export interface ProductVariant extends ProductVariantDiff, ProductDiff, ProductRoot {
    /** same as cart.sapOfferCode, but on ProductVariant
     * @name sapUoId
     * @see sapOfferCode */
    sapUoId?: string;
    /** displays product category: "Ausinės", "Kolonėlės", etc.
     *  similar to Category.name but persists in Upsell Product references
     * @see Category */
    categoryName: string;
}

export type DeliveryMode = {
    code: string;
    deliveryCost: Price;
    description: string;
    name: string;
    paymentMethods: CheckoutPaymentMethod[];
};

export type Gift = {
    code: string;
    description: string;
    energyConsumingSupported?: boolean;
    images: ProductImageData[];
    legalText?: string;
    name: string;
    price: PriceBase;
    purchasable: boolean;
    scalaId: string;
    shortDescription: string;
    shortDescription2: string;
    shortDescription3: string;
    standardPrice: PriceBase;
    stock: Stock;
};

export type Benefit = {
    gift: Gift;
    showOldPriceCrossed: boolean;
};
type ConsumedEntry = {
    adjustedUnitPrice: number;
    orderEntryNumber: number;
    quantity: number;
};
export type Promotion = {
    benefits: Benefit[];
    code: string;
    giftRequirementType: GiftRequirementType;
    couldFireMessages?: unknown[];
    firedMessages?: unknown[];
};
export interface PromotionResultData {
    consumedEntries: ConsumedEntry[];
    description: string;
    promotion: Promotion;
}

type Country = {
    isocode: string;
    name: string;
};

export enum DocumentType {
    OrderAgreement = 'ZOA',
    SodraDocument = 'ZCUSTAPPS',
}

export type OrderDocument = {
    documentId: string;
    documentStatus: DocumentStatus;
    documentType: DocumentType;
};

export enum DocumentStatus {
    New = 'NEW',
    PhysicalSignature = 'PHYSICAL_SIGNATURE',
    Valid = 'VALID',
    Cancelled = 'CANCELLED',
    Archived = 'ARCHIVED',
    Signed = 'SIGNED',
    NotSigned = 'NOT_SIGNED',
    Ready = 'READY',
}

export enum GeneratedUrlType {
    Configurator = 'CONFIGURATOR',
    Upsell = 'UPSELL',
    Order = 'ORDER',
}

export enum FailureReason {
    Payment = 'PAYMENT',
    System = 'SYSTEM',
}
export enum OrderStatus {
    Completed = 'COMPLETED',
    Cancelled = 'CANCELLED',
    Cancelling = 'CANCELLING',
    Open = 'OPEN',
    InitCreditCheckFailed = 'INIT_CREDIT_CHECK_FAILED',
    InitCreditCheckFailedFinal = 'INIT_CREDIT_CHECK_FAILED_FINAL',
    CreditCheckFailedFinal = 'CREDIT_CHECK_FAILED_FINAL',
    CreditDocSignInProgress = 'CREDIT_DOC_SIGN_IN_PROGRESS',
    CreditDocSigned = 'CREDIT_DOC_SIGNED',
    OADocSignInProgress = 'OA_DOC_SIGN_IN_PROGRESS',
}
export enum CommitmentType {
    NoCommitment = 'FULL',
    Commitment12 = 'MONTHS_12',
    Commitment24 = 'MONTHS_24',
}

export type AddressExtra = {
    cellphone: string;
    country: Country;
    defaultAddress: boolean;
    email: string;
    formattedAddress: string;
    extAddressNumber: string;
    id: string;
    line1: string;
    line2: string;
    phone: string;
    postalCode: string;
    shippingAddress: boolean;
    town: string;
    visibleInAddressBook: boolean;
    comment?: string;
};

type SpecialOfferBase = { icon: IconName | string; title: string; description: string };

export type SingleSpecialOffer = SpecialOfferBase & {
    single: true;
    image: ProductImageData;
    imagePosition: 'LEFT' | 'TOP';
};

export type GiftSpecialOffer = SpecialOfferBase & {
    single: false;
};

export type ProductSpecialOffer = SingleSpecialOffer | GiftSpecialOffer;

export interface ProductDeviceInCart extends ProductVariantDiff, ProductDiff {
    baseWarrantyType?: string;
    description?: string;
    discountPrice?: Price;
    legalText?: string;
    productSpecialOffers: ProductSpecialOffer[];
    /** @name standardPrice
     * crossed-out price do display
     * @see selectProductOldTotalPrice */
    standardPrice?: Price;
    manufacturerWarranty?: string;
    extendedWarranties?: ExtendedWarranty[];
}

type EntryBase = {
    /** @name basePrice
     * full price on one piece of the product at the moment recalculation was done. e.g. for placed order it remains even if product price has been changed
     * ( also same as .baseOptions[0].selected.priceData OR cart.entries[].product.price OR when buying in Full-Price same as cart.entries[].totalPrice )
     * @see ProductOption.priceData
     * @see ProductDeviceInCart.price
     * @see EntryBase.totalPrice
     * @see selectProductBasePrice */
    basePrice: Price;
    copyrightFee: Price;
    copyrightFeeWithTax: Price;
    entryNumber: number;
    entryType?: CartEntryType;
    giveAway: boolean;
    monthlyPrice: Price;
    nboBonusText?: string;
    product: unknown;
    reverseVatApplied: boolean;
    quantity: number;
    /** @see EntryBase.basePrice */
    totalPrice: Price;
    totalPriceWithTax: Price;
    monthlyPriceWithTax: Price;
    totalFullPriceWithDiscount: Price;
    totalPriceWithoutDiscount?: Price;
    totalPriceWithoutDiscountWithTax?: Price;
    updateable: boolean;
};

export type EntryWithDevice = EntryBase & {
    entryType: CartEntryType.PurchasableProduct;
    product: ProductDeviceInCart;
    rentingPlanEntry?: RentingPlan;
    extendedWarrantyType: string;
    extendedWarrantyPrice: Price;
    futureStock: boolean;
    serviceBundleDiscount?: Price;
};
export type EntryWithRatePlan = EntryBase & { entryType: CartEntryType.RatePlan; product: ProductRatePlanInCart };
export type EntryWithUpsell = EntryBase & { entryType: CartEntryType.UpsellProduct; product: ProductVariant };
export type EntryWithSimCard = EntryBase & { entryType: CartEntryType.SimCard; product: ProductVariant };
export type EntryWithESimCard = EntryBase & { entryType: CartEntryType.EsimCard; product: ProductVariant };
export type EntryWithGift = EntryBase & { entryType: undefined; product: ProductVariant };
export type EntryWithNetflix = EntryBase & { entryType: CartEntryType.Netflix; product: ProductVariant };
export type EntryWithDisney = EntryBase & { entryType: CartEntryType.Disney; product: ProductVariant };

export type CartEntry =
    | EntryWithDevice
    | EntryWithRatePlan
    | EntryWithUpsell
    | EntryWithSimCard
    | EntryWithGift
    | EntryWithESimCard
    | EntryWithNetflix
    | EntryWithDisney;

export type User = {
    name: string;
    uid: string;
};

type Voucher = {
    code: string;
    freeShipping: boolean;
    voucherCode: string;
    leasingDiscountRemoved: boolean;
    giftDiscountRemoved: boolean;
};

export type ConsentTemplate = {
    message?: string;
    type?: string;
    version?: number;
    code?: string;
    title?: string;
    terms?: string;
    consentTemplateGroup?: boolean;
    consentTemplates?: ConsentTemplate[];
};

export type SubscriptionOrder = {
    subscriberId: string;
    msisdn: string;
    status: string;
};

export type InitialPaymentOption = {
    initialPaymentPrice: Price;
    title: string;
};

interface FreshCart {
    availablePaymentMethods: CheckoutPaymentMethod[];
    code: string;
    commitment?: CommitmentType;
    consentTemplates: ConsentTemplate[];
    entries: CartEntry[];
    /** @name generated
     * indicates that the Cart is read-only,
     * serves as a template which can be "cloned", a cloned cart then can be used in normal checkout lifecycle */
    generated: boolean;
    guid: string;
    /** @name hasInstallments
     * @deprecated Do Not Use! - it's falsy.
     */
    hasInstallments: boolean;
    offerCode: string;
    selectedGiftProductCodes: string[];
    selectedInitialPayment: number;
    totalItems: number;
    totalMonthlyPrice: Price;
    totalMonthlyPriceWithoutRatePlan: Price;
    totalPrice: PriceBase;
    totalPriceWithTax: PriceBase;
    type: string;
    withInsurance: boolean;
}

export interface Cart extends FreshCart, SavePlanOrderData, CompanyData, VatConfig {
    appliedOrderPromotions: PromotionResultData[];
    appliedProductPromotions: PromotionResultData[];
    appliedVouchers: Voucher[];
    calculated: boolean;
    contractType: ContractType;
    couponsDiscounts: Price;
    deliveryAddress: AddressExtra;
    displayOrderNumber?: string;
    extend: boolean;
    maxInitialPayment?: Price;
    paymentAddress: AddressExtra;
    courierNote: string;
    /** @name creditCheckResult
     * after placing Order in Leasing payment, shows error message key of "credit check" */
    creditCheckResult?: { errorMessageKey: string };
    deliveryCost: Price;
    deliveryCostWithTax: Price;
    deliveryMode: DeliveryMode;
    failureReason?: FailureReason;
    freePeriodOfInsurancePayment: number;
    initialPaymentOptions: InitialPaymentOption[];
    selectedInitialPaymentOption: InitialPaymentOption;
    insuranceMonthlyPrice: Price;
    insuranceFranchisePrice: Price;
    netflixPremiumMonthlyPrice?: Price;
    disneyPremiumMonthlyPrice?: Price;
    isPaperlessOn?: boolean;
    /** @name createdFromPublicWeb
     * used to verify that the eShop Checkout request came from Public Web
     * when ordering MBB Plan with a modem device */
    createdFromPublicWeb?: boolean;
    leasingPlan?: LeasingPlan;
    monthlyPrice: Price;
    net: boolean;
    orderDiscounts: Price;
    orderDocuments?: OrderDocument[];
    orderRevenue: Price;
    status?: OrderStatus;
    paymentBankTitle?: string;
    pickupItemsQuantity: number;
    pickupOrderGroups: unknown[];
    /** @name popPaymentInfo
     * indicates the payment via POP status, usually set at the end of Checkout */
    popPaymentInfo?: { status: string };
    potentialOfferPromotions: Promotion[];
    potentialOrderPromotions?: PromotionResultData[];
    potentialProductPromotions: unknown[];
    promotionLegalText: string;
    possibleContractTypes: ContractType[];
    possibleLeasingPlans: LeasingPlan[];
    possibleRentingPlans: RentingPlan[];
    possibleRatePlans: ProductRatePlan[];
    /** @name possibleSapRatePlans
     * @description new kind of Enhanced RatePlans, include "cart.features", might fall back to "cart.possibleRatePlans"
     * @see {cart.features}
     * */
    possibleSapRatePlans?: SapRatePlan[];
    features?: ServiceFeature[];
    bundleDiscountAvailable?: boolean;
    productDiscounts: Price;
    reverseVatApplied: boolean;
    /** @name sapOfferCode
     * used to determine if product is a "Special offer" from SAP catalog, empty field means NOT */
    sapOfferCode?: string;
    site: string;
    store: string;
    subTotal: Price;
    totalDiscounts: Price;
    totalPrice: Price;
    totalPriceWithTax: Price;
    totalMonthlyPriceWithTax: Price;
    /** @name totalFirstBill
     * first bill price is feature toggle-able */
    totalFirstBill: Price;
    reverseVatApplicable: boolean;
    totalTax: Price;
    totalUnitCount: number;
    user: User;
    leasingPlansAvailability: LeasingPlanAvailability;
    leasingPlansAvailabilityWithoutPlan?: LeasingPlanAvailability;
    checkoutPaymentType: CheckoutPaymentMethod;
    /** @name generatedUrlType
     * this attribute appears only on a cart which was previously "cloned"
     * @see generated */
    generatedUrlType?: GeneratedUrlType;
    couponMessage?: string;
    /** @name esimAvailable
     * toggles visibility of the SIM/eSIM card selection combo-box in ClientData forms
     * */
    esimAvailable: boolean;
    esimOnly: boolean;
    subscriptionOrderList?: SubscriptionOrder[];
    guestCheckoutPossible?: boolean;
}

export enum LeasingPlanAvailability {
    Available = 'AVAILABLE',
    Linked = 'LINKED',
    NotAvailable = 'NOT_AVAILABLE',
}

export type ExtendedWarranty = {
    durationInMonths: number;
    period: string;
    price: Price;
    priceType: string;
};

export type CartSession = {
    code: string;
    guid: string;
    isCartAnonymous: boolean;
    isPreviouslyCloned?: boolean;
};

export type TermsOfUseTextResponse = {
    description: string;
    id: string;
    version: number;
};

export type RentingPlan = {
    monthlyOldPrice?: Price;
    monthlyPrice: Price;
    residualPrice: Price;
    residualPriceWithTax: Price;
    rentingPeriod: RentingPeriod;
    monthValue: number;
    vasProducts?: VasProduct[];
};

export type InsuranceVAS = {
    title: string;
    description: string;
    vasProducts: VasProduct[];
};

export type AccountSubscriptionOption =
    | 'BLOCKING_IN_PROGRESS'
    | 'SIM_CARD'
    | 'ADDITIONAL_SERVICES'
    | 'CALL_AND_SMS_SETTINGS'
    | 'E_SIM'
    | 'PERMISSIONS';

export enum RentingPeriod {
    Months12 = 'MONTHS_12',
    Months24 = 'MONTHS_24',
    Months36 = 'MONTHS_36',
}

export enum CartMessage {
    TermsOfUse = 'TERMS_OF_USE',
}

export enum CheckoutPaths {
    ClientData = '/kliento-duomenys',
    Delivery = '/pristatymas',
    Documents = '/dokumentai',
    Payment = '/apmokejimas',
    OrderConfirmation = '/uzsakymo-patvirtinimas',
    OrderError = '/uzsakymo-klaida',
}

export enum StepNames {
    ClientData = 'client_data',
    Delivery = 'delivery',
    Payment = 'payment',
    OrderConfirmation = 'order_confirmation',
    OrderError = 'order_error',
}

export type CardType = CartEntryType.EsimCard | CartEntryType.SimCard | null;

export enum CartErrorTypes {
    VoucherError = 'VoucherOperationError',
    CartError = 'CartError',
    LocalizedMessageError = 'LocalizedMessageError',
    ConnectionError = 'ConnectionError',
    OutOfStock = 'InsufficientStockLevelError',
    OutOfStockIlm = 'IlmInsufficientStockLevelError',
    OutOfStockColt = 'ColtInsufficientStockLevelError',
    OutOfStockLocalReservation = 'CommerceCartOutOfStockError',
    CartBookingColtError = 'ColtCartBookingError',
    CartBookingIlmError = 'IlmCartBookingError',
    UnknownIdentifierError = 'UnknownIdentifierError',
    InterimPaymentError = 'InterimPaymentError',
    FailedPaymentError = 'FailedPaymentError',
}

export enum KnownReason {
    NotFound = 'notFound',
    Expired = 'expired',
}

export type CommonCartErrorShape = {
    errors: Partial<{
        message: string;
        reason?: KnownReason;
        type: CartErrorTypes;
    }>[];
};

export type AxiosCartError = AxiosError<CommonCartErrorShape>;

export type CartError = {
    type: CartErrorTypes;
    reason?: KnownReason;
    message?: string;
    status?: number;
    data?: any;
    time: Date;
};

export enum CheckoutRejectionErrorType {
    General = 'general',
    PersonalCode = 'personal-code',
    Payment = 'payment',
    OutOfStock = 'out-of-stock',
}

export type NetflixPlan = 'NETFLIX_STANDARD_PRODUCT' | 'NETFLIX_PREMIUM_PRODUCT';
export type DisneyPlan = 'DISNEY_STANDARD_PRODUCT' | 'DISNEY_PREMIUM_PRODUCT';
